"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, CheckCircle2, Clock } from "lucide-react";

export default function VerifyEmailPage() {
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const supabase = createClient();

  useEffect(() => {
    // Check if user is already logged in and email is verified
    checkEmailStatus();
  }, []);

  async function checkEmailStatus() {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user?.email_confirmed_at) {
      // Email is already verified, redirect to appropriate dashboard
      const userType = user.user_metadata?.type;
      if (userType === 'contractor') {
        router.push('/contractor/dashboard');
      } else if (userType === 'worker') {
        router.push('/worker/dashboard');
      } else {
        router.push('/dashboard');
      }
    }
  }

  async function resendVerificationEmail() {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError("No user found. Please log in again.");
        setLoading(false);
        return;
      }
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email as string,
      });
      
      if (error) {
        setError(error.message);
      } else {
        setEmailSent(true);
      }
    } catch (error) {
      setError("Failed to resend verification email.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
            <Mail className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle className="mt-6 text-3xl font-extrabold text-gray-900">
            Check your email
          </CardTitle>
          <CardDescription className="mt-2 text-sm text-gray-600">
            We've sent a verification email to your address. Please check your inbox and click the link to verify your email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <Clock className="h-5 w-5 text-blue-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Didn't receive the email?</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>Check your spam folder or click the button below to resend the verification email.</p>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {emailSent && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Email sent!</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>We've resent the verification email. Please check your inbox.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <Button
              onClick={resendVerificationEmail}
              disabled={loading || emailSent}
              className="w-full"
              variant="outline"
            >
              {loading ? "Sending..." : emailSent ? "Email sent" : "Resend verification email"}
            </Button>

            <Button
              onClick={() => router.push('/auth/login')}
              variant="ghost"
              className="w-full"
            >
              Back to login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
